import { createContext, createEffect, onCleanup, Show, useContext, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"

type Registration = {
  active: () => boolean
  register: () => void
  unregister: () => void
}

type TitlebarRightSlot = {
  occupied: () => boolean
  createRegistration: () => Registration
  mount: () => HTMLElement | undefined
  setMount: (mount: HTMLElement) => void
}

const TitlebarRightContext = createContext<TitlebarRightSlot>()

export function TitlebarRightProvider(props: ParentProps) {
  return (
    <TitlebarRightContext.Provider value={createTitlebarRightSlot()}>{props.children}</TitlebarRightContext.Provider>
  )
}

export function createTitlebarRightSlot(): TitlebarRightSlot {
  const [store, setStore] = createStore<{ mount?: HTMLElement; registrations: symbol[] }>({ registrations: [] })
  return {
    occupied: () => store.registrations.length > 0,
    mount: () => store.mount,
    setMount: (mount) => setStore("mount", mount),
    createRegistration() {
      const id = Symbol()
      return {
        active: () => store.registrations.at(-1) === id,
        register: () => setStore("registrations", (items) => [...items, id]),
        unregister: () => setStore("registrations", (items) => items.filter((item) => item !== id)),
      }
    },
  }
}

export function TitlebarRightMount(props: ParentProps<{ vertical?: boolean }>) {
  const slot = useTitlebarRightSlot()
  return (
    <div
      ref={slot.setMount}
      id="opencode-titlebar-right"
      class={props.vertical ? "flex w-full shrink-0 flex-col" : "flex min-w-0 flex-1 items-center justify-end gap-0"}
    >
      <Show when={!slot.occupied()}>{props.children}</Show>
    </div>
  )
}

export function TitlebarRight(props: ParentProps<{ active?: boolean }>) {
  // Isolated component previews do not have desktop chrome.
  const slot = useContext(TitlebarRightContext)
  if (!slot) return props.children
  const registration = slot.createRegistration()
  createEffect(() => {
    if (props.active === false) return
    registration.register()
    onCleanup(registration.unregister)
  })

  return (
    <Show when={registration.active() && slot.mount()} keyed>
      {(mount) => <Portal mount={mount}>{props.children}</Portal>}
    </Show>
  )
}

function useTitlebarRightSlot() {
  const slot = useContext(TitlebarRightContext)
  if (!slot) throw new Error("TitlebarRight must be used within TitlebarRightProvider")
  return slot
}
